import type { Breed } from 'shared/@types';
import { getSafeLanguageCode } from 'shared/library/i18n/languages';
import { buildRoute } from 'shared/library/routes/route';

type CanonicalBreedPage =
  | { name: 'breeds.show' }
  | { name: 'breeds.show.breeders' }
  | { name: 'breeds.show.listings' }
  | { name: 'breeds.show.listings.by-city'; city: string };

type CanonicalBreedPageRedirectOptions = {
  breed: Pick<Breed, 'slug'>;
  requestedSlug: string;
  locale?: string;
  hasWebResponse: boolean;
  page: CanonicalBreedPage;
};

function getCanonicalBreedSlug(breed: Pick<Breed, 'slug'>, locale?: string): string {
  const languageCode = getSafeLanguageCode(locale);

  return breed.slug[languageCode] ?? breed.slug.en;
}

function buildCanonicalBreedPageDestination(
  page: CanonicalBreedPage,
  canonicalSlug: string,
  locale?: string,
): string {
  const lang = getSafeLanguageCode(locale);

  switch (page.name) {
    case 'breeds.show':
      return buildRoute(page.name, { breed: canonicalSlug }, { lang });
    case 'breeds.show.breeders':
      return buildRoute(page.name, { breed: canonicalSlug }, { lang });
    case 'breeds.show.listings':
      return buildRoute(page.name, { breed: canonicalSlug }, { lang });
    case 'breeds.show.listings.by-city':
      return buildRoute(page.name, { breed: canonicalSlug, city: page.city }, { lang });
  }
}

function getCanonicalBreedPageRedirect({
  breed,
  requestedSlug,
  locale,
  hasWebResponse,
  page,
}: CanonicalBreedPageRedirectOptions) {
  if (!hasWebResponse) {
    return null;
  }

  const canonicalSlug = getCanonicalBreedSlug(breed, locale);

  if (requestedSlug === canonicalSlug) {
    return null;
  }

  return {
    redirect: {
      destination: buildCanonicalBreedPageDestination(page, canonicalSlug, locale),
      statusCode: 301 as const,
    },
  };
}

export { getCanonicalBreedPageRedirect, getCanonicalBreedSlug };
export type { CanonicalBreedPage };
